import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { API_URL, apiFetch } from '../api';

/**
 * Centralized data fetching + granular realtime subscriptions.
 */
export function useSupabaseData() {
  const [goals, setGoals]             = useState([]);
  const [milestones, setMilestones]   = useState([]);
  const [tasks, setTasks]             = useState([]);
  const [inbox, setInbox]             = useState([]);
  const [agents, setAgents]           = useState([]);
  const [agentLog, setAgentLog]       = useState([]);
  const [conflicts, setConflicts]     = useState([]);
  const [routines, setRoutines]       = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [liveCalendar, setLiveCalendar] = useState([]);
  const [founders, setFounders]       = useState([]);
  const [meetings, setMeetings]       = useState([]);
  const [meetingActions, setMeetingActions] = useState([]);
  const [folders, setFolders]         = useState([]);
  const [scheduledBlocks, setScheduledBlocks] = useState([]);
  const [roles, setRoles]             = useState([]);
  const [promises, setPromises]       = useState([]);
  const [meetingBriefs, setMeetingBriefs] = useState([]);
  const [loading, setLoading]         = useState(false);
  const schedulerTimer = useRef(null);

  const fetchCalendar = useCallback(async (force = false) => {
    if (!API_URL) return;
    try {
      const res = await apiFetch(`/calendar${force ? '?force=1' : ''}`);
      if (res.ok) {
        const { events } = await res.json();
        if (events?.length) setLiveCalendar(events);
      }
    } catch {
      // Local server not running
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const weekEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const [
      { data: g },
      { data: m },
      { data: t },
      { data: ib },
      { data: ag },
      { data: al },
      { data: cf },
      { data: rt },
      { data: ins },
      { data: fn },
      { data: mt },
      { data: ma },
      { data: fl },
      { data: sb },
      { data: rl },
      { data: pr },
      { data: mb },
    ] = await Promise.all([
      supabase.from('goals').select('*, roles(slug, label, colour), goal_members(id, member_role, member:profiles!user_id(id, full_name, email, avatar_url))').eq('status', 'active').order('sort_order'),
      supabase.from('milestones').select('*').order('sort_order'),
      supabase.from('tasks').select('*, goals(title, priority, status, roles(slug, label)), roles(slug, label, colour), milestones(id, title, is_active, stage_status), assignee:profiles!assignee_id(id, full_name, email, avatar_url)').order('priority').order('deadline', { ascending: true, nullsFirst: false }).order('created_at', { ascending: true }),
      supabase.from('inbox_items').select('*').order('sort_order', { ascending: true, nullsFirst: false }).order('created_at', { ascending: false }).limit(50),
      supabase.from('agents').select('*').order('sort_order'),
      supabase.from('agent_log').select('*, agents(name)').order('created_at', { ascending: false }).limit(20),
      supabase.from('conflicts').select('*').eq('is_dismissed', false).order('created_at', { ascending: false }),
      supabase.from('routines').select('*').order('sort_order'),
      supabase.from('agent_instructions').select('*').order('created_at', { ascending: false }).limit(50),
      supabase.from('founders').select('*').eq('is_active', true).order('sort_order'),
      supabase.from('meetings').select('*, founders(name, company_name)').order('meeting_date', { ascending: false }).limit(200),
      supabase.from('meeting_actions').select('*').order('created_at', { ascending: false }),
      supabase.from('folders').select('*').order('sort_order'),
      supabase.from('scheduled_blocks').select('*, tasks(id, title, priority, duration_minutes, status, deadline, role_id, goal_id, assignee_id, roles(slug, label, colour), assignee:profiles!assignee_id(id, full_name, email, avatar_url))').gte('date', today).lte('date', weekEnd).order('date').order('start_min'),
      supabase.from('roles').select('*').order('sort_order'),
      supabase.from('promises').select('*, roles(slug, label, colour)').order('committed_at', { ascending: false }).limit(500),
      supabase.from('meeting_briefs').select('*').gte('meeting_start', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()).order('meeting_start'),
    ]);
    setGoals(g || []);
    setMilestones(m || []);
    setTasks(t || []);
    setInbox(ib || []);
    setAgents(ag || []);
    setAgentLog(al || []);
    setConflicts(cf || []);
    setRoutines(rt || []);
    setInstructions(ins || []);
    setFounders(fn || []);
    setMeetings(mt || []);
    setMeetingActions(ma || []);
    setFolders(fl || []);
    setScheduledBlocks(sb || []);
    setRoles(rl || []);
    setPromises(pr || []);
    setMeetingBriefs(mb || []);
    // Load cached calendar immediately, then force-refresh in background
    fetchCalendar(false).then(() => fetchCalendar(true));
    setLoading(false);
  }, [fetchCalendar]);

  useEffect(() => { load(); }, [load]);

  // Granular realtime — reload only the affected table
  useEffect(() => {
    const reloadTable = (table) => async () => {
      switch (table) {
        case 'goals':
        case 'goal_members': {
          const { data } = await supabase.from('goals').select('*, roles(slug, label, colour), goal_members(id, member_role, member:profiles!user_id(id, full_name, email, avatar_url))').eq('status', 'active').order('sort_order');
          if (data) setGoals(data);
          break;
        }
        case 'milestones': {
          const { data } = await supabase.from('milestones').select('*').order('sort_order');
          if (data) setMilestones(data);
          break;
        }
        case 'tasks': {
          const { data } = await supabase.from('tasks').select('*, goals(title, priority, roles(slug, label)), roles(slug, label), milestones(id, title), assignee:profiles!assignee_id(id, full_name, email, avatar_url)').order('priority').order('sort_order');
          if (data) setTasks(data);
          break;
        }
        case 'inbox_items': {
          const { data } = await supabase.from('inbox_items').select('*').order('sort_order', { ascending: true, nullsFirst: false }).order('created_at', { ascending: false }).limit(50);
          if (data) setInbox(data);
          break;
        }
        case 'agents': {
          const { data } = await supabase.from('agents').select('*').order('sort_order');
          if (data) setAgents(data);
          break;
        }
        case 'agent_log': {
          const { data } = await supabase.from('agent_log').select('*, agents(name)').order('created_at', { ascending: false }).limit(20);
          if (data) setAgentLog(data);
          break;
        }
        case 'conflicts': {
          const { data } = await supabase.from('conflicts').select('*').eq('is_dismissed', false).order('created_at', { ascending: false });
          if (data) setConflicts(data);
          break;
        }
        case 'routines': {
          const { data } = await supabase.from('routines').select('*').order('sort_order');
          if (data) setRoutines(data);
          break;
        }
        case 'agent_instructions': {
          const { data } = await supabase.from('agent_instructions').select('*').order('created_at', { ascending: false }).limit(50);
          if (data) setInstructions(data);
          break;
        }
        case 'founders': {
          const { data } = await supabase.from('founders').select('*').eq('is_active', true).order('sort_order');
          if (data) setFounders(data);
          break;
        }
        case 'meetings': {
          const { data } = await supabase.from('meetings').select('*, founders(name, company_name)').order('meeting_date', { ascending: false }).limit(200);
          if (data) setMeetings(data);
          break;
        }
        case 'meeting_actions': {
          const { data } = await supabase.from('meeting_actions').select('*').order('created_at', { ascending: false });
          if (data) setMeetingActions(data);
          break;
        }
        case 'folders': {
          const { data } = await supabase.from('folders').select('*').order('sort_order');
          if (data) setFolders(data);
          break;
        }
        case 'scheduled_blocks': {
          const today = new Date().toISOString().split('T')[0];
          const weekEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          const { data } = await supabase.from('scheduled_blocks').select('*, tasks(id, title, priority, duration_minutes, status, deadline, role_id, goal_id, assignee_id, roles(slug, label, colour), assignee:profiles!assignee_id(id, full_name, email, avatar_url))').gte('date', today).lte('date', weekEnd).order('date').order('start_min');
          if (data) setScheduledBlocks(data);
          break;
        }
        case 'promises': {
          const { data } = await supabase.from('promises').select('*, roles(slug, label, colour)').order('committed_at', { ascending: false }).limit(500);
          if (data) setPromises(data);
          break;
        }
        case 'meeting_briefs': {
          const { data } = await supabase.from('meeting_briefs').select('*').gte('meeting_start', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()).order('meeting_start');
          if (data) setMeetingBriefs(data);
          break;
        }
        default:
          break;
      }
    };

    const tables = ['goals', 'goal_members', 'milestones', 'tasks', 'inbox_items', 'agents', 'agent_log', 'conflicts', 'routines', 'agent_instructions', 'founders', 'meetings', 'meeting_actions', 'folders', 'scheduled_blocks', 'promises', 'meeting_briefs'];
    let channel = supabase.channel('realtime-granular');

    tables.forEach(table => {
      channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, reloadTable(table));
    });

    channel.subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  // Inbox operations (optimistic updates + persist)
  const markRead = (id) => {
    setInbox(prev => prev.map(i => i.id === id ? { ...i, is_read: true } : i));
    supabase.from('inbox_items').update({ is_read: true }).eq('id', id).then();
  };

  const markUnread = (id) => {
    setInbox(prev => prev.map(i => i.id === id ? { ...i, is_read: false } : i));
    supabase.from('inbox_items').update({ is_read: false }).eq('id', id).then();
  };

  const archiveItem = (id, archive = true) => {
    setInbox(prev => prev.map(i => i.id === id ? { ...i, is_archived: archive } : i));
    supabase.from('inbox_items').update({ is_archived: archive }).eq('id', id).then();
  };

  const deleteItem = (id) => {
    setInbox(prev => prev.filter(i => i.id !== id));
    supabase.from('inbox_items').delete().eq('id', id).then();
  };

  const reorderItems = (fromId, toId) => {
    setInbox(prev => {
      const items = [...prev];
      const fromIdx = items.findIndex(i => i.id === fromId);
      const toIdx = items.findIndex(i => i.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);
      items.forEach((item, idx) => {
        supabase.from('inbox_items').update({ sort_order: idx }).eq('id', item.id).then();
      });
      return items;
    });
  };

  // Debounced scheduler trigger — call after task/project mutations
  const triggerScheduler = useCallback(() => {
    if (!API_URL) return;
    if (schedulerTimer.current) clearTimeout(schedulerTimer.current);
    schedulerTimer.current = setTimeout(async () => {
      try { await apiFetch('/scheduler/run', { method: 'POST' }); } catch {}
    }, 2000);
  }, []);

  return {
    goals, milestones, tasks, inbox, agents, agentLog,
    conflicts, routines, instructions, liveCalendar,
    founders, meetings, meetingActions,
    folders, scheduledBlocks, roles,
    promises, meetingBriefs,
    loading, load, fetchCalendar, triggerScheduler,
    markRead, markUnread, archiveItem, deleteItem, reorderItems,
  };
}
